import { labelFromValue } from '@shared/helpers/string.helpers';
import { HOURS_PER_YEAR } from '../../../constants/math.constants';
import { IElementCategory } from '../../../models/element-categories.model';
import {
  ElementPropertyType,
  type IElementPropertyOption,
} from '../../../models/element-property.model';
import {
  ACTIVITY_VENTILATION_HOURS_VARIABLE,
  AHU_DEFAULT_HEAT_RECOVERY_EFFICIENCY,
  AHU_DEFAULT_SPECIFIC_FAN_POWER,
  AirHandlingUnitPropertyName,
  POST_HEATING_ENERGY_SOURCES,
} from './air-handling-unit.model';
import { DEFAULT_VENT_FLOW_PER_M2 } from '@shared/helpers/energy/energy.models';
import { EXPRESSION_VARIABLE_NAME } from '@shared/constants/expression-variables-names.constant';
import { commonElementCategoryProperties } from '../common-element-category-properties.model';

/** Canonical options for {@link AirHandlingUnitPropertyName.PostHeatingEnergySource} (value = generic product id). */
export const ahuPostHeatingEnergySourceSelectOptions: IElementPropertyOption[] =
  Object.entries(POST_HEATING_ENERGY_SOURCES).map(([key, value]) => ({
    value,
    label: labelFromValue(key),
  }));

/**
 * Post-heating properties. The toggle property but also optional properties
 */
const postHeatingProperties: IElementCategory['properties'] = [
  {
    name: AirHandlingUnitPropertyName.SupplyAirPostHeating,
    type: ElementPropertyType.Switch,
    count: false,
    description:
      'If the supply air should be heated before being supplied to the rooms.',
  },
  {
    conditions: { [AirHandlingUnitPropertyName.SupplyAirPostHeating]: true },
    nodes: [
      {
        name: AirHandlingUnitPropertyName.PostHeatingTemperature,
        displayName: 'Temperature',
        type: ElementPropertyType.Expression,
        unit: '°C',
        min: 5,
        fallbackCount: 18,
        max: 75,
        description: 'The temperature of the supplied air after post heating.',
        indentation: 1,
      },
      {
        name: AirHandlingUnitPropertyName.PostHeatingEnergySource,
        displayName: 'Energy source',
        type: ElementPropertyType.Select,
        options: ahuPostHeatingEnergySourceSelectOptions,
        indentation: 1,
        description: 'The energy source of the post heating.',
      },
    ],
  },
];

/**
 * Pre-cooling properties. The toggle property but also optional properties. TODO: Reapply when adding AC (soon)
 */
// const preCoolingProperties: IElementCategory['properties'] = [
//   {
//     name: AirHandlingUnitPropertyName.SupplyAirPreCooling,
//     type: ElementPropertyType.Switch,
//     count: false,
//     description: 'If the supply air should be cooled.',
//   },
//   {
//     conditions: { [AirHandlingUnitPropertyName.SupplyAirPreCooling]: true },
//     nodes: [
//       {
//         name: AirHandlingUnitPropertyName.PreCoolingTemperature,
//         displayName: 'Temperature',
//         type: ElementPropertyType.Expression,
//         unit: '°C',
//         fallbackCount: 14,
//         min: 0,
//         max: 20,
//         description: 'The temperature of air after cooling.',
//         indentation: 1,
//       },
//       {
//         name: AirHandlingUnitPropertyName.PreCoolingHours,
//         displayName: 'Cooling hours',
//         type: ElementPropertyType.Expression,
//         unit: 'hr',
//         displayUnit: 'hrs/year',
//         min: 0,
//         max: HOURS_PER_YEAR,
//         fallbackCount: LOCATION_COOLING_HOURS_VARIABLE,
//         description:
//           'How many hours per year the air is cooled. Default is based on how many hours cooling is needed at the location and type of activity. Like offices is only occuped 9 hours a day 5 days a week.',
//         indentation: 1,
//       },
//       {
//         name: AirHandlingUnitPropertyName.PreCoolingEffectRatio,
//         displayName: 'Energy effect ratio',
//         type: ElementPropertyType.Expression,
//         fallbackCount: 5,
//         min: 0,
//         max: 10,
//         unit: 'none',
//         indentation: 1,
//         description: `How effective cooling is. An effect ratio of 5 means that 5 kWh of heat is removed from the air for every kWh of electricy`,
//       },
//     ],
//   },
// ];

/**
 * All air handling unit properties
 */
export const ahuProperties: IElementCategory['properties'] = [
  {
    name: AirHandlingUnitPropertyName.VentilationArea,
    type: ElementPropertyType.Expression,
    fallbackCount: `${EXPRESSION_VARIABLE_NAME.HEATED_AREA}`,
    min: 0,
    unit: 'm²',
    description: 'The total area this unit should serve.',
  },
  {
    name: AirHandlingUnitPropertyName.SpecificFanPower,
    min: 0,
    fallbackCount: AHU_DEFAULT_SPECIFIC_FAN_POWER,
    type: ElementPropertyType.Expression,
    unit: 'kW/m³s',
    description: 'How much power in kW the fan consumes per m³/s of airflow.',
  },
  {
    name: AirHandlingUnitPropertyName.Airflow,
    type: ElementPropertyType.Expression,
    unit: 'm³/s',
    description: 'How much air the the unit can supply each second',
    fallbackCount: `${AirHandlingUnitPropertyName.VentilationArea} * ${DEFAULT_VENT_FLOW_PER_M2}`,
  },

  // {
  //   name: AirHandlingUnitPropertyName.SupplyAirFlowPerGFA,
  //   type: ElementPropertyType.Expression,
  //   min: 0,
  //   fallbackCount: DEFAULT_VENT_FLOW_PER_M2,
  //   unit: 'l/m²s',
  //   description: 'How much supply airflow per m² of floor area.',
  // },
  // {
  //   name: AirHandlingUnitPropertyName.ExhaustAirFlowPerGFA,
  //   type: ElementPropertyType.Expression,
  //   fallbackCount: AirHandlingUnitPropertyName.SupplyAirFlowPerGFA,
  //   min: 0,
  //   unit: 'l/m²s',
  //   description: 'How much exhaust airflow per m² of floor area.',
  // },
  {
    name: AirHandlingUnitPropertyName.OperationHours,
    type: ElementPropertyType.Expression,
    fallbackCount: `${ACTIVITY_VENTILATION_HOURS_VARIABLE}`,
    min: 0,
    max: HOURS_PER_YEAR,
    unit: 'hr',
    displayUnit: 'hrs/year',
    description:
      'How many hours per year the air handling unit is in operation. Defaults 24/7 for apartments and office hours for offices and schools.',
  },
  ...postHeatingProperties,
  {
    name: AirHandlingUnitPropertyName.HeatRecoveryEfficiency,
    type: ElementPropertyType.Expression,
    fallbackCount: AHU_DEFAULT_HEAT_RECOVERY_EFFICIENCY,
    min: 0,
    max: 1,
    unit: '%',
    description:
      'How much heat recovery efficiency the air handling unit has. 100% means that all heat from the exhaust air is recovered and used to preheat the supply air.',
  },
  // ...preCoolingProperties,
  ...commonElementCategoryProperties,
];
